/**
 * Reports Page
 *
 * Main page for the ServerliCCM Reports module.
 * Handles report library, configuration, generation, preview, and management.
 *
 * v2 -- Frontend polish pass:
 *   - Custom dark-themed Recharts tooltips
 *   - Colored stat cards with left border accents
 *   - Chart drill-down (click bar -> filter Detailed tab)
 *   - Improved hover states
 *   - Empty state for Detailed tab
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FileBarChart, Monitor, Video, DoorOpen, ScanFace, Users, ClipboardList,
  Plus, Calendar, Download, Mail, Trash2, Play, Eye, FileSpreadsheet,
  ChevronRight, Clock, AlertCircle, CheckCircle, XCircle, Loader2, RefreshCw,
  Settings, FileText, ArrowLeft, Search, Filter, X, HelpCircle
} from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import reportsTutorial from '../components/tutorials/configs/reportsTutorial';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, LineChart, Line, Legend
} from 'recharts';
import useReportsStore from '../stores/reportsStore';
import { MODULES } from '../components/reports/reportModules';
import ReportPickerWizard from '../components/reports/ReportPickerWizard';
import '../styles/v2/pages/reports.css';

const ICON_MAP = {
  Monitor, Video, DoorOpen, ScanFace, Users, ClipboardList, FileBarChart
};

const CATEGORY_COLORS = {
  core: { bg: 'rgba(34, 197, 94, 0.12)', color: '#22c55e' },
  integration: { bg: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' },
  admin: { bg: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b' },
};

const STATUS_CONFIG = {
  QUEUED: { icon: Clock, color: '#94a3b8', label: 'Queued' },
  GENERATING: { icon: Loader2, color: '#3b82f6', label: 'Generating', spin: true },
  COMPLETED: { icon: CheckCircle, color: '#22c55e', label: 'Completed' },
  FAILED: { icon: XCircle, color: '#ef4444', label: 'Failed' },
  EXPIRED: { icon: AlertCircle, color: '#64748b', label: 'Expired' },
};

const DATE_RANGE_OPTIONS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

const CHART_COLORS = ['#00d4aa', '#3b82f6', '#a78bfa', '#f59e0b', '#ec4899', '#ef4444', '#22c55e', '#64748b'];

// Stat card accent color mapping
const STAT_ACCENTS = {
  teal: { border: '#00d4aa', bg: 'rgba(0, 212, 170, 0.06)' },
  green: { border: '#22c55e', bg: 'rgba(34, 197, 94, 0.06)' },
  red: { border: '#ef4444', bg: 'rgba(239, 68, 68, 0.06)' },
  amber: { border: '#f59e0b', bg: 'rgba(245, 158, 11, 0.06)' },
  blue: { border: '#3b82f6', bg: 'rgba(59, 130, 246, 0.06)' },
  purple: { border: '#a78bfa', bg: 'rgba(167, 139, 250, 0.06)' },
};

export default function Reports() {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(reportsTutorial);
  const {
    reportTypes, reportRuns, runsTotal, currentRun, currentReportData,
    templates, schedules, loading, generating, error, activeTab, selectedType,
    setActiveTab, setSelectedType, clearError,
    fetchReportTypes, generateReport, fetchReportRuns, fetchReportRun,
    deleteReport, downloadReport, fetchTemplates, fetchSchedules, reset,
    createTemplate, deleteTemplate, createSchedule, deleteSchedule,
  } = useReportsStore();

  const [view, setView] = useState('library'); // library | config | preview
  const [wizardModule, setWizardModule] = useState(null);
  const [configParams, setConfigParams] = useState({
    dateRange: '7d',
    format: 'PDF',
    includeCharts: true,
    groupBy: 'deviceType',
  });
  const [previewMode, setPreviewMode] = useState('summary');
  // Drill-down filter: { key: 'date'|'deviceType', value: string }
  const [chartFilter, setChartFilter] = useState(null);
  const pendingAutoDownloadRef = useRef(null);

  useEffect(() => {
    fetchReportTypes();
    fetchReportRuns();
    return () => reset();
  }, []);

  useEffect(() => {
    if (activeTab === 'templates') fetchTemplates();
    if (activeTab === 'scheduled') fetchSchedules();
    if (activeTab === 'generated') fetchReportRuns();
  }, [activeTab]);

  useEffect(() => {
    const pending = pendingAutoDownloadRef.current;
    if (pending && currentRun && currentRun.id === pending.runId && currentRun.status === 'COMPLETED') {
      downloadReport(pending.runId, pending.format.toLowerCase());
      pendingAutoDownloadRef.current = null;
    }
  }, [currentRun, downloadReport]);

  const handleSelectType = (type) => {
    setSelectedType(type);
    setView('config');
  };

  const handleGenerate = async () => {
    if (!selectedType) return;
    try {
      const run = await generateReport(selectedType.id, configParams, configParams.format);
      setActiveTab('generated');
      setView('library');
    } catch (err) {
      // Error handled by store
    }
  };

  const handleViewReport = async (runId) => {
    await fetchReportRun(runId);
    setChartFilter(null);
    setPreviewMode('summary');
    setView('preview');
  };

  const handleWizardGenerate = async (reportType, params, format) => {
    try {
      if (params.saveAsTemplate && params.templateName) {
        await createTemplate({
          name: params.templateName, type: reportType.id,
          parameters: params, format, shared: false,
        });
      }
      const run = await generateReport(reportType.id, params, format);
      if (params.createFile) {
        pendingAutoDownloadRef.current = { runId: run.id, format };
      }
      setWizardModule(null);
      await handleViewReport(run.id);
    } catch (err) {
      // Error already surfaced by the store into the `error` state; keep the
      // wizard open so the user can retry, don't close on failure
    }
  };

  const handleWizardCreateSchedule = async (reportType, params) => {
    try {
      const recipients = (params.recipientsInput || '')
        .split(',').map(s => s.trim()).filter(Boolean);
      if (params.saveAsTemplate && params.templateName) {
        await createTemplate({
          name: params.templateName, type: reportType.id,
          parameters: params, format: params.format, shared: false,
        });
      }
      await createSchedule({
        name: params.scheduleName, type: reportType.id, parameters: params,
        format: params.format, frequency: params.frequency,
        dayOfWeek: params.dayOfWeek, dayOfMonth: params.dayOfMonth,
        timeOfDay: params.timeOfDay, recipients,
      });
      setWizardModule(null);
      setActiveTab('scheduled');
    } catch (err) {
      // error already surfaced by the store, keep wizard open to retry
    }
  };

  const handleBack = () => {
    if (view === 'preview') {
      setView('library');
      setActiveTab('generated');
      setChartFilter(null);
    } else if (view === 'config') {
      setView('library');
      setSelectedType(null);
    }
  };

  // Drill-down handler: click chart element -> set filter + switch to detailed
  const handleChartDrillDown = useCallback((filterKey, filterValue) => {
    if (chartFilter && chartFilter.key === filterKey && chartFilter.value === filterValue) {
      // Toggle off if clicking same bar again
      setChartFilter(null);
    } else {
      setChartFilter({ key: filterKey, value: filterValue });
      setPreviewMode('detailed');
    }
  }, [chartFilter]);

  const clearChartFilter = useCallback(() => setChartFilter(null), []);

  return (
    <div className="reports-page">
      {/* Page Header */}
      <div className="reports-header">
        <div className="reports-header-left">
          {view !== 'library' && (
            <button className="reports-back-btn" onClick={handleBack}>
              <ArrowLeft size={18} />
            </button>
          )}
          <div>
            <h1 className="page-title">
              {view === 'config' && selectedType ? (
                <span className="reports-breadcrumb">
                  <span className="breadcrumb-parent" onClick={handleBack}>Reports</span>
                  <ChevronRight size={16} />
                  {selectedType.name}
                </span>
              ) : view === 'preview' && currentRun ? (
                <span className="reports-breadcrumb">
                  <span className="breadcrumb-parent" onClick={handleBack}>Reports</span>
                  <ChevronRight size={16} />
                  {currentRun.type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              ) : 'Reports'}
            </h1>
            <p className="page-subtitle">
              {view === 'config' ? 'Configure report parameters' :
               view === 'preview' ? (currentRun ? `Generated ${new Date(currentRun.createdAt).toLocaleDateString()}` : '') :
               'Generate, schedule, and export reports across all platform modules'}
            </p>
          </div>
        </div>
        <div className="reports-header-actions">
          <button className="tutorial-help-btn" onClick={() => startTutorial(reportsTutorial.module, reportsTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
          {view === 'config' && (
            <>
              <button className="btn btn-secondary" onClick={() => { setView('library'); setActiveTab('generated'); }}>
                <Eye size={16} /> Preview
              </button>
              <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}>
                {generating ? <Loader2 size={16} className="spin" /> : <Play size={16} />}
                {generating ? 'Generating...' : 'Generate report'}
              </button>
            </>
          )}
          {view === 'preview' && currentRun?.status === 'COMPLETED' && (
            <>
              <button className="btn btn-secondary" onClick={() => downloadReport(currentRun.id, 'pdf')}>
                <Download size={16} /> Export PDF
              </button>
              <button className="btn btn-secondary" onClick={() => downloadReport(currentRun.id, 'xlsx')}>
                <FileSpreadsheet size={16} /> Export XLSX
              </button>
            </>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="reports-error">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={clearError}><XCircle size={14} /></button>
        </div>
      )}

      {/* Main Content */}
      {view === 'library' && <LibraryView
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        reportTypes={reportTypes}
        reportRuns={reportRuns}
        templates={templates}
        schedules={schedules}
        onSelectType={handleSelectType}
        onSelectModule={setWizardModule}
        onViewReport={handleViewReport}
        onDeleteReport={deleteReport}
        onDownload={downloadReport}
        onRefresh={fetchReportRuns}
        onDeleteSchedule={deleteSchedule}
        onDeleteTemplate={deleteTemplate}
      />}

      {view === 'config' && selectedType && <ConfigView
        reportType={selectedType}
        params={configParams}
        setParams={setConfigParams}
        onGenerate={handleGenerate}
        generating={generating}
      />}

      {view === 'preview' && <PreviewView
        run={currentRun}
        reportData={currentReportData}
        loading={loading}
        previewMode={previewMode}
        setPreviewMode={setPreviewMode}
        chartFilter={chartFilter}
        onChartDrillDown={handleChartDrillDown}
        onClearFilter={clearChartFilter}
      />}

      {showPrompt && (
        <TutorialPrompt
          module={reportsTutorial.module}
          moduleName={reportsTutorial.moduleName}
          steps={reportsTutorial.steps}
          onClose={dismissTutorial}
        />
      )}

      <ReportPickerWizard
        isOpen={!!wizardModule}
        module={wizardModule}
        reportTypes={reportTypes}
        generating={generating}
        onClose={() => setWizardModule(null)}
        onSelectAndConfigure={handleWizardGenerate}
        onCreateSchedule={handleWizardCreateSchedule}
      />
    </div>
  );
}

// ============================================
// Custom Recharts Tooltip
// ============================================
function ChartTooltip({ active, payload, label, labelFormatter, valueFormatter }) {
  if (!active || !payload || !payload.length) return null;
  const displayLabel = labelFormatter ? labelFormatter(label) : label;
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-label">{displayLabel}</div>
      {payload.map((entry, i) => (
        <div key={i} className="chart-tooltip-row">
          <span className="chart-tooltip-dot" style={{ background: entry.color || entry.fill }} />
          <span className="chart-tooltip-name">{entry.name || entry.dataKey}</span>
          <span className="chart-tooltip-value">
            {valueFormatter ? valueFormatter(entry.value) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ============================================
// Custom Recharts Legend
// ============================================
function ChartLegend({ payload }) {
  if (!payload || !payload.length) return null;
  return (
    <div className="chart-legend">
      {payload.map((entry, i) => (
        <div key={i} className="chart-legend-item">
          <span className="chart-legend-dot" style={{ background: entry.color }} />
          <span className="chart-legend-text">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================
// Library View (Tabs: Library, Generated, Scheduled, Templates)
// ============================================
function LibraryView({ activeTab, setActiveTab, reportTypes, reportRuns, templates, schedules, onSelectType, onSelectModule, onViewReport, onDeleteReport, onDownload, onRefresh, onDeleteSchedule, onDeleteTemplate }) {
  return (
    <>
      <div className="reports-tabs">
        {['library', 'generated', 'scheduled', 'templates'].map(tab => (
          <button
            key={tab}
            className={`reports-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'library' ? 'Report library' :
             tab === 'generated' ? 'Generated' :
             tab === 'scheduled' ? 'Scheduled' : 'Templates'}
          </button>
        ))}
      </div>

      {activeTab === 'library' && (
        <div className="report-type-grid module-grid">
          {MODULES.map(mod => {
            const IconComp = ICON_MAP[mod.icon] || FileBarChart;
            const availableCount = mod.reportIds.filter(id => reportTypes.some(rt => rt.id === id)).length;
            const badgeLabel = availableCount > 0
              ? `${availableCount} available`
              : `${mod.comingSoon.length} coming soon`;
            const isDisabled = mod.reportIds.length === 0;
            return (
              <div
                key={mod.id}
                className={`report-type-card ${isDisabled ? 'disabled' : ''}`}
                onClick={() => !isDisabled && onSelectModule(mod)}
              >
                <div className="rtc-icon" style={{ background: `${mod.color}20`, color: mod.color }}>
                  <IconComp size={20} />
                </div>
                <div className="rtc-badge" style={{ background: 'rgba(148, 163, 184, 0.12)', color: '#94a3b8' }}>
                  {badgeLabel}
                </div>
                <div className="rtc-name">{mod.name}</div>
                <div className="rtc-desc">{mod.description}</div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'generated' && (
        <GeneratedTab runs={reportRuns} onView={onViewReport} onDelete={onDeleteReport} onDownload={onDownload} onRefresh={onRefresh} />
      )}

      {activeTab === 'scheduled' && (
        <ScheduledTab schedules={schedules} onDelete={onDeleteSchedule} />
      )}

      {activeTab === 'templates' && (
        <TemplatesTab templates={templates} onDelete={onDeleteTemplate} />
      )}
    </>
  );
}

// ============================================
// Generated Reports Tab
// ============================================
function GeneratedTab({ runs, onView, onDelete, onDownload, onRefresh }) {
  if (!runs.length) {
    return (
      <div className="reports-empty-state">
        <FileBarChart size={48} />
        <h3>No reports generated yet</h3>
        <p>Select a report type from the library to generate your first report.</p>
      </div>
    );
  }

  return (
    <div className="generated-runs">
      <div className="generated-header">
        <span className="generated-count">{runs.length} reports</span>
        <button className="btn btn-icon" onClick={onRefresh} title="Refresh">
          <RefreshCw size={16} />
        </button>
      </div>
      <table className="runs-table">
        <thead>
          <tr>
            <th>Report</th>
            <th>Type</th>
            <th>Status</th>
            <th>Generated</th>
            <th>Size</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {runs.map(run => {
            const statusCfg = STATUS_CONFIG[run.status] || STATUS_CONFIG.QUEUED;
            const StatusIcon = statusCfg.icon;
            return (
              <tr key={run.id}>
                <td className="run-name" onClick={() => run.status === 'COMPLETED' && onView(run.id)}>
                  {run.type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
                </td>
                <td>
                  <span className="type-badge" style={{ background: `${getTypeColor(run.type)}20`, color: getTypeColor(run.type) }}>
                    {run.format}
                  </span>
                </td>
                <td>
                  <span className="status-badge" style={{ color: statusCfg.color }}>
                    <StatusIcon size={14} className={statusCfg.spin ? 'spin' : ''} />
                    {statusCfg.label}
                  </span>
                </td>
                <td className="run-date">{new Date(run.createdAt).toLocaleString()}</td>
                <td>{run.fileSize ? formatBytes(run.fileSize) : '--'}</td>
                <td className="run-actions">
                  {run.status === 'COMPLETED' && (
                    <>
                      <button className="btn-icon-sm" title="View" onClick={() => onView(run.id)}>
                        <Eye size={14} />
                      </button>
                      <button className="btn-icon-sm" title="Download PDF" onClick={() => onDownload(run.id, 'pdf')}>
                        <Download size={14} />
                      </button>
                    </>
                  )}
                  <button className="btn-icon-sm danger" title="Delete" onClick={() => onDelete(run.id)}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// Scheduled Reports Tab
// ============================================
function ScheduledTab({ schedules, onDelete }) {
  if (!schedules.length) {
    return (
      <div className="reports-empty-state">
        <Calendar size={48} />
        <h3>No scheduled reports yet</h3>
        <p>Select a report type from the library and choose "Schedule recurring" to set one up.</p>
      </div>
    );
  }

  return (
    <div className="generated-runs">
      <div className="generated-header">
        <span className="generated-count">{schedules.length} schedule{schedules.length !== 1 ? 's' : ''}</span>
      </div>
      <table className="runs-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Report type</th>
            <th>Frequency</th>
            <th>Next run</th>
            <th>Recipients</th>
            <th>Active</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {schedules.map(schedule => {
            const recipientCount = schedule.recipients?.length || 0;
            return (
              <tr key={schedule.id}>
                <td className="run-name">{schedule.name}</td>
                <td>
                  <span className="type-badge" style={{ background: `${getTypeColor(schedule.type)}20`, color: getTypeColor(schedule.type) }}>
                    {schedule.type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                </td>
                <td>{schedule.frequency.charAt(0) + schedule.frequency.slice(1).toLowerCase()}</td>
                <td className="run-date">{schedule.nextRunAt ? new Date(schedule.nextRunAt).toLocaleString() : '--'}</td>
                <td>{recipientCount > 0 ? `${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}` : 'None'}</td>
                <td>
                  <span className="status-badge" style={{ color: schedule.active ? '#22c55e' : '#64748b' }}>
                    {schedule.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="run-actions">
                  <button className="btn-icon-sm danger" title="Delete" onClick={() => onDelete(schedule.id)}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// Report Templates Tab
// ============================================
function TemplatesTab({ templates, onDelete }) {
  if (!templates.length) {
    return (
      <div className="reports-empty-state">
        <FileText size={48} />
        <h3>No templates saved yet</h3>
        <p>Save your frequently used report configurations as templates for quick access.</p>
      </div>
    );
  }

  return (
    <div className="generated-runs">
      <div className="generated-header">
        <span className="generated-count">{templates.length} template{templates.length !== 1 ? 's' : ''}</span>
      </div>
      <table className="runs-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Report type</th>
            <th>Format</th>
            <th>Shared</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {templates.map(template => (
            <tr key={template.id}>
              <td className="run-name">{template.name}</td>
              <td>
                <span className="type-badge" style={{ background: `${getTypeColor(template.type)}20`, color: getTypeColor(template.type) }}>
                  {template.type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              </td>
              <td>{template.format}</td>
              <td>
                <span className="status-badge" style={{ color: template.shared ? '#00d4aa' : '#64748b' }}>
                  {template.shared ? 'Shared' : 'Private'}
                </span>
              </td>
              <td className="run-actions">
                <button className="btn-icon-sm danger" title="Delete" onClick={() => onDelete(template.id)}>
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// Configuration View
// ============================================
function ConfigView({ reportType, params, setParams, onGenerate, generating }) {
  const updateParam = (key, value) => setParams(prev => ({ ...prev, [key]: value }));

  return (
    <div className="config-panel">
      <div className="config-panel-title">
        <Settings size={18} style={{ color: '#00d4aa' }} />
        Report parameters
      </div>
      <div className="config-grid">
        <div className="config-field">
          <label>Date range</label>
          <select value={params.dateRange} onChange={e => updateParam('dateRange', e.target.value)}>
            {DATE_RANGE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="config-field">
          <label>Output format</label>
          <select value={params.format} onChange={e => updateParam('format', e.target.value)}>
            <option value="PDF">PDF</option>
            <option value="XLSX">Excel (XLSX)</option>
            <option value="HTML">HTML (Preview)</option>
          </select>
        </div>
        <div className="config-field">
          <label>Include charts</label>
          <select value={params.includeCharts ? 'yes' : 'no'} onChange={e => updateParam('includeCharts', e.target.value === 'yes')}>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        {reportType.id === 'SYSTEM_HEALTH' && (
          <div className="config-field">
            <label>Group by</label>
            <select value={params.groupBy || 'deviceType'} onChange={e => updateParam('groupBy', e.target.value)}>
              <option value="deviceType">Device type</option>
              <option value="site">Site</option>
              <option value="none">No grouping</option>
            </select>
          </div>
        )}
      </div>
      <div className="config-actions">
        <button className="btn btn-primary" onClick={onGenerate} disabled={generating}>
          {generating ? <Loader2 size={16} className="spin" /> : <Play size={16} />}
          {generating ? 'Generating...' : 'Generate now'}
        </button>
      </div>
      <div className="config-hint">
        <AlertCircle size={14} />
        Large date ranges may take a few moments to generate. You will receive a notification when complete.
      </div>
    </div>
  );
}

// ============================================
// Preview View
// ============================================
function PreviewView({ run, reportData, loading, previewMode, setPreviewMode, chartFilter, onChartDrillDown, onClearFilter }) {
  if (loading) {
    return (
      <div className="reports-loading">
        <Loader2 size={32} className="spin" />
        <p>Loading report...</p>
      </div>
    );
  }

  if (!run) {
    return <div className="reports-empty-state"><AlertCircle size={48} /><h3>Report not found</h3></div>;
  }

  if (run.status === 'GENERATING') {
    return (
      <div className="reports-loading">
        <Loader2 size={32} className="spin" />
        <p>Report is still generating...</p>
      </div>
    );
  }

  if (run.status === 'FAILED') {
    return (
      <div className="reports-empty-state error">
        <XCircle size={48} />
        <h3>Report generation failed</h3>
        <p>{run.error || 'An unknown error occurred'}</p>
      </div>
    );
  }

  const data = reportData;

  return (
    <div className="report-preview">
      <div className="preview-header">
        <div className="preview-header-left">
          <span className="preview-title">Overview</span>
          {chartFilter && (
            <button className="filter-chip" onClick={onClearFilter}>
              <Filter size={12} />
              Filtered: {chartFilter.key} = {chartFilter.value}
              <X size={12} />
            </button>
          )}
        </div>
        <div className="preview-toggle">
          {['summary', 'detailed', 'raw'].map(mode => (
            <button
              key={mode}
              className={`toggle-btn ${previewMode === mode ? 'active' : ''}`}
              onClick={() => setPreviewMode(mode)}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="preview-body">
        {previewMode === 'summary' && data && (
          <SummaryView data={data} type={run.type} onDrillDown={onChartDrillDown} activeFilter={chartFilter} />
        )}
        {previewMode === 'detailed' && data && (
          <DetailedView data={data} type={run.type} filter={chartFilter} onClearFilter={onClearFilter} />
        )}
        {previewMode === 'raw' && data && <RawView data={data} />}
        {!data && (
          <div className="reports-empty-state">
            <FileText size={48} />
            <h3>Report data not available for preview</h3>
            <p>Download the generated file to view the full report.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Summary View (Stat Cards + Charts)
// ============================================
function SummaryView({ data, type, onDrillDown, activeFilter }) {
  const { summary, charts } = data;

  if (data.metadata?.stub) {
    return (
      <div className="reports-empty-state">
        <Clock size={48} />
        <h3>This report type isn't built yet</h3>
        <p>{summary?.message || 'This report is on the roadmap but the data aggregator has not been implemented yet.'}</p>
      </div>
    );
  }

  if (!summary) return null;

  const handleBarClick = (filterKey) => (data) => {
    if (data && data[filterKey] !== undefined) {
      onDrillDown(filterKey, data[filterKey]);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    // Show "Mar 15" style
    const parts = d.split('-');
    if (parts.length === 3) {
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${months[parseInt(parts[1], 10) - 1]} ${parseInt(parts[2], 10)}`;
    }
    return d.slice(5);
  };

  return (
    <>
      <div className="preview-stats">
        {type === 'SYSTEM_HEALTH' && (
          <>
            <StatCard label="Total devices" value={summary.totalDevices} accent="teal" />
            <StatCard label="Avg uptime" value={`${summary.avgUptime}%`} accent="green" />
            <StatCard label="Offline devices" value={summary.offlineDevices} accent={summary.offlineDevices > 0 ? 'red' : 'teal'} />
            <StatCard label="Active alerts" value={summary.activeAlerts} accent={summary.activeAlerts > 0 ? 'amber' : 'teal'} />
          </>
        )}
        {type === 'USER_ACTIVITY' && (
          <>
            <StatCard label="Total actions" value={summary.totalActions} accent="blue" />
            <StatCard label="Unique users" value={summary.uniqueUsers} accent="purple" />
            <StatCard label="Total logins" value={summary.totalLogins} accent="teal" />
            <StatCard label="Failed logins" value={summary.failedLogins} accent={summary.failedLogins > 0 ? 'red' : 'teal'} />
          </>
        )}
        {type === 'ACCESS_CONTROL_USERS' && (
          <>
            <StatCard label="Active cardholders" value={summary.totalActiveCardholders} accent="purple" />
            <StatCard label="Access level groups" value={summary.totalGroups} accent="teal" />
            <StatCard label="Unassigned" value={summary.unassignedCount} accent={summary.unassignedCount > 0 ? 'amber' : 'teal'} />
          </>
        )}
        {type === 'EXPIRING_CARDS' && (
          <>
            <StatCard label="Expiring soon" value={summary.totalExpiring} accent={summary.totalExpiring > 0 ? 'amber' : 'teal'} />
            <StatCard label="Already expired" value={summary.totalExpired} accent={summary.totalExpired > 0 ? 'red' : 'teal'} />
            <StatCard label="Threshold (days)" value={summary.thresholdDays} accent="teal" />
          </>
        )}
        {type === 'REGISTRATIONS_BY_DATE' && (
          <>
            <StatCard label="Registered" value={summary.totalRegistered} accent="green" />
            <StatCard label="Migrated" value={summary.totalMigrated} accent={summary.totalMigrated > 0 ? 'amber' : 'teal'} />
            <StatCard label="Total in range" value={summary.totalInRange} accent="teal" />
          </>
        )}
        {type === 'DEVICE_MONITOR_EVENTS' && (
          <>
            <StatCard label="Total events" value={summary.totalEvents} accent="teal" />
            <StatCard label="Critical" value={summary.critical} accent={summary.critical > 0 ? 'red' : 'teal'} />
            <StatCard label="Warning" value={summary.warning} accent={summary.warning > 0 ? 'amber' : 'teal'} />
          </>
        )}
        {!['SYSTEM_HEALTH', 'USER_ACTIVITY', 'ACCESS_CONTROL_USERS', 'EXPIRING_CARDS', 'REGISTRATIONS_BY_DATE', 'DEVICE_MONITOR_EVENTS'].includes(type) && summary && (
          Object.entries(summary).slice(0, 4).map(([key, value]) => (
            <StatCard key={key} label={key.replace(/([A-Z])/g, ' $1').trim().replace(/^./, c => c.toUpperCase())} value={value} accent="teal" />
          ))
        )}
      </div>

      {/* Charts -- System Health */}
      {charts?.alertsByDay && type === 'SYSTEM_HEALTH' && (
        <div className={`preview-chart-card ${activeFilter?.key === 'date' ? 'chart-active-filter' : ''}`}>
          <div className="chart-card-header">
            <h3>Alerts over time</h3>
            {activeFilter?.key === 'date' && (
              <span className="chart-filter-hint">Click a bar to drill down into that day</span>
            )}
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.alertsByDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: '#64748b', fontSize: 11 }}
                tickFormatter={formatDate}
                axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={35}
              />
              <Tooltip content={<ChartTooltip labelFormatter={formatDate} />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Bar dataKey="count" fill="#00d4aa" radius={[6, 6, 0, 0]} name="Alerts" maxBarSize={40} onClick={handleBarClick('date')} style={{ cursor: 'pointer' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {charts?.devicesByType && type === 'SYSTEM_HEALTH' && (
        <div className={`preview-chart-card ${activeFilter?.key === 'type' ? 'chart-active-filter' : ''}`}>
          <div className="chart-card-header">
            <h3>Devices by type</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.devicesByType}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="type"
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={35}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Legend content={<ChartLegend />} />
              <Bar dataKey="online" fill="#22c55e" stackId="a" name="Online" maxBarSize={40} onClick={handleBarClick('type')} style={{ cursor: 'pointer' }} />
              <Bar dataKey="offline" fill="#ef4444" stackId="a" name="Offline" maxBarSize={40} onClick={handleBarClick('type')} style={{ cursor: 'pointer' }} />
              <Bar dataKey="degraded" fill="#f59e0b" stackId="a" radius={[6, 6, 0, 0]} name="Degraded" maxBarSize={40} onClick={handleBarClick('type')} style={{ cursor: 'pointer' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Charts -- User Activity */}
      {charts?.activityByDay && type === 'USER_ACTIVITY' && (
        <div className="preview-chart-card">
          <div className="chart-card-header">
            <h3>Activity over time</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={charts.activityByDay}>
              <defs>
                <linearGradient id="gradActions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00d4aa" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#00d4aa" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradLogins" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: '#64748b', fontSize: 11 }}
                tickFormatter={formatDate}
                axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={35}
              />
              <Tooltip content={<ChartTooltip labelFormatter={formatDate} />} cursor={{ stroke: 'rgba(255,255,255,0.1)' }} />
              <Legend content={<ChartLegend />} />
              <Area type="monotone" dataKey="total" stroke="#00d4aa" strokeWidth={2} fill="url(#gradActions)" name="Actions" />
              <Area type="monotone" dataKey="logins" stroke="#3b82f6" strokeWidth={2} fill="url(#gradLogins)" name="Logins" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Charts -- Access Control Users */}
      {charts?.cardholdersByGroup && type === 'ACCESS_CONTROL_USERS' && (
        <div className={`preview-chart-card ${activeFilter?.key === 'groupName' ? 'chart-active-filter' : ''}`}>
          <div className="chart-card-header">
            <h3>Cardholders by access level</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.cardholdersByGroup}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="groupName"
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={35}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Bar dataKey="count" fill="#a78bfa" radius={[6, 6, 0, 0]} name="Cardholders" maxBarSize={40} onClick={handleBarClick('groupName')} style={{ cursor: 'pointer' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {charts?.assignedVsUnassigned && type === 'ACCESS_CONTROL_USERS' && (
        <div className="preview-chart-card">
          <div className="chart-card-header">
            <h3>Assigned vs unassigned</h3>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Tooltip content={<ChartTooltip />} />
              <Legend content={<ChartLegend />} />
              <Pie
                data={charts.assignedVsUnassigned}
                dataKey="value"
                nameKey="name"
                innerRadius="60%"
                outerRadius="85%"
              >
                {charts.assignedVsUnassigned.map((entry) => (
                  <Cell key={entry.name} fill={entry.name === 'Unassigned' ? '#f59e0b' : '#a78bfa'} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Charts -- Registrations by Date */}
      {charts?.registrationsByDay && type === 'REGISTRATIONS_BY_DATE' && (
        <div className={`preview-chart-card ${activeFilter?.key === 'day' ? 'chart-active-filter' : ''}`}>
          <div className="chart-card-header">
            <h3>Registrations by day</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.registrationsByDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="day"
                tick={{ fill: '#64748b', fontSize: 11 }}
                tickFormatter={formatDate}
                axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={35}
              />
              <Tooltip content={<ChartTooltip labelFormatter={formatDate} />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Legend content={<ChartLegend />} />
              <Bar dataKey="registered" fill="#22c55e" stackId="a" name="Registered" maxBarSize={40} onClick={handleBarClick('day')} style={{ cursor: 'pointer' }} />
              <Bar dataKey="migrated" fill="#f59e0b" stackId="a" radius={[6, 6, 0, 0]} name="Migrated" maxBarSize={40} onClick={handleBarClick('day')} style={{ cursor: 'pointer' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Charts -- Expiring Cards */}
      {charts?.expiringByPeriod && type === 'EXPIRING_CARDS' && (
        <div className="preview-chart-card">
          <div className="chart-card-header">
            <h3>Cards expiring by period</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.expiringByPeriod}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="period"
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={35}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Bar dataKey="count" fill="#f59e0b" radius={[6, 6, 0, 0]} name="Cards" maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Charts -- Device Monitor Events */}
      {charts?.eventsByDay && type === 'DEVICE_MONITOR_EVENTS' && (
        <div className={`preview-chart-card ${activeFilter?.key === 'date' ? 'chart-active-filter' : ''}`}>
          <div className="chart-card-header">
            <h3>Events over time</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.eventsByDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: '#64748b', fontSize: 11 }}
                tickFormatter={formatDate}
                axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={35}
              />
              <Tooltip content={<ChartTooltip labelFormatter={formatDate} />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Bar dataKey="count" fill="#00d4aa" radius={[6, 6, 0, 0]} name="Events" maxBarSize={40} onClick={handleBarClick('date')} style={{ cursor: 'pointer' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {charts?.eventsByConditionType && type === 'DEVICE_MONITOR_EVENTS' && (
        <div className={`preview-chart-card ${activeFilter?.key === 'conditionType' ? 'chart-active-filter' : ''}`}>
          <div className="chart-card-header">
            <h3>Events by condition type</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.eventsByConditionType}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="conditionType"
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={35}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Bar dataKey="count" fill="#a78bfa" radius={[6, 6, 0, 0]} name="Events" maxBarSize={40} onClick={handleBarClick('conditionType')} style={{ cursor: 'pointer' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );
}

function StatCard({ label, value, accent = 'teal' }) {
  const colors = STAT_ACCENTS[accent] || STAT_ACCENTS.teal;

  return (
    <div className="stat-card" style={{ borderLeftColor: colors.border, background: colors.bg }}>
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value" style={{ color: colors.border }}>{value}</div>
    </div>
  );
}

function matchesSearch(text, needle) {
  if (!needle) return true;
  return String(text || '').toLowerCase().includes(needle.trim().toLowerCase());
}

// ============================================
// Detailed View (Tables) with drill-down filtering
// ============================================
function DetailedView({ data, type, filter, onClearFilter }) {
  const { details } = data;

  if (type === 'SYSTEM_HEALTH') {
    const rows = details?.deviceTable || [];

    // Apply drill-down filter
    const filtered = useMemo(() => {
      if (!filter || !rows.length) return rows;
      if (filter.key === 'type' || filter.key === 'deviceType') {
        return rows.filter(d => d.deviceType && d.deviceType.toLowerCase() === filter.value.toLowerCase());
      }
      if (filter.key === 'date') {
        // Filter by devices that had alerts on that date (show all devices but highlight is implicit)
        // Since device table doesn't have per-day data, show all with a note
        return rows;
      }
      return rows;
    }, [rows, filter]);

    const [searchText, setSearchText] = useState('');
    const [deviceTypeFilter, setDeviceTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    useEffect(() => {
      setSearchText('');
      setDeviceTypeFilter('');
      setStatusFilter('');
    }, [rows]);

    if (!rows.length) {
      return (
        <div className="reports-empty-state">
          <Monitor size={48} />
          <h3>No device data available</h3>
          <p>This tenant has no discovered or monitored devices to report on. Deploy an agent and run a network scan to populate device data.</p>
        </div>
      );
    }

    const deviceTypes = [...new Set(rows.map(r => r.deviceType).filter(Boolean))].sort();
    const statuses = [...new Set(rows.map(r => r.status).filter(Boolean))].sort();

    const displayedRows = filtered
      .filter(r => !deviceTypeFilter || r.deviceType === deviceTypeFilter)
      .filter(r => !statusFilter || r.status === statusFilter)
      .filter(r => matchesSearch(r.name, searchText));

    const searchFilterActive = !!(searchText || deviceTypeFilter || statusFilter);

    return (
      <div className="detail-table-wrap">
        {filter && (
          <div className="detail-filter-bar">
            <Filter size={14} />
            <span>Showing {filtered.length} of {rows.length} devices</span>
            {filter.key === 'type' && <span className="filter-value">Type: {filter.value}</span>}
            {filter.key === 'date' && <span className="filter-value">Date: {filter.value}</span>}
            <button className="filter-clear-btn" onClick={onClearFilter}>
              <X size={12} /> Clear
            </button>
          </div>
        )}
        <div className="detail-search-bar">
          <input type="text" placeholder="Search devices..." value={searchText}
            onChange={e => setSearchText(e.target.value)} className="detail-search-input" />
          <select value={deviceTypeFilter} onChange={e => setDeviceTypeFilter(e.target.value)}>
            <option value="">All types</option>
            {deviceTypes.map(dt => <option key={dt} value={dt}>{dt}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <table className="detail-table">
          <thead>
            <tr>
              <th>Device</th>
              <th>Type</th>
              <th>Status</th>
              <th>IP Address</th>
              <th>Alerts</th>
              <th>Last seen</th>
            </tr>
          </thead>
          <tbody>
            {searchFilterActive && displayedRows.length === 0 ? (
              <tr className="detail-no-results"><td colSpan={6}>No results match your filters.</td></tr>
            ) : displayedRows.map((d, i) => (
              <tr key={i}>
                <td className="td-name">{d.name}</td>
                <td><span className={`device-badge ${d.deviceType}`}>{d.deviceType}</span></td>
                <td><span className={`status-indicator ${d.status}`}>{d.status}</span></td>
                <td className="td-mono">{d.ipAddress || '--'}</td>
                <td className={d.alertCount > 0 ? 'td-warn' : ''}>{d.alertCount}</td>
                <td className="td-date">{d.lastSeen ? new Date(d.lastSeen).toLocaleString() : '--'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === 'USER_ACTIVITY') {
    const rows = details?.userActivityTable || [];

    const [searchText, setSearchText] = useState('');
    const [roleFilter, setRoleFilter] = useState('');

    useEffect(() => {
      setSearchText('');
      setRoleFilter('');
    }, [rows]);

    if (!rows.length) {
      return (
        <div className="reports-empty-state">
          <Users size={48} />
          <h3>No user activity data</h3>
          <p>No user actions or logins were recorded during the selected date range. Try expanding the date range or check that audit logging is enabled.</p>
        </div>
      );
    }

    const roles = [...new Set(rows.map(r => r.role).filter(Boolean))].sort();

    const displayedRows = rows
      .filter(r => !roleFilter || r.role === roleFilter)
      .filter(r => matchesSearch(r.userName, searchText) || matchesSearch(r.email, searchText));

    const searchFilterActive = !!(searchText || roleFilter);

    return (
      <div className="detail-table-wrap">
        <div className="detail-search-bar">
          <input type="text" placeholder="Search users..." value={searchText}
            onChange={e => setSearchText(e.target.value)} className="detail-search-input" />
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
            <option value="">All roles</option>
            {roles.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <table className="detail-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Actions</th>
              <th>Logins</th>
              <th>Last activity</th>
            </tr>
          </thead>
          <tbody>
            {searchFilterActive && displayedRows.length === 0 ? (
              <tr className="detail-no-results"><td colSpan={6}>No results match your filters.</td></tr>
            ) : displayedRows.map((u, i) => (
              <tr key={i}>
                <td className="td-name">{u.userName}</td>
                <td>{u.email}</td>
                <td><span className="role-badge">{u.role}</span></td>
                <td>{u.totalActions}</td>
                <td>{u.logins}</td>
                <td className="td-date">{u.lastActivity ? new Date(u.lastActivity).toLocaleString() : '--'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === 'ACCESS_CONTROL_USERS') {
    const rows = details?.usersByAccessLevelTable || [];

    const [searchText, setSearchText] = useState('');

    useEffect(() => {
      setSearchText('');
    }, [rows]);

    if (!rows.length) {
      return (
        <div className="reports-empty-state">
          <Users size={48} />
          <h3>No active cardholders found</h3>
          <p>No cardholders were returned from Feenics. Check that the Feenics integration is connected and has people assigned.</p>
        </div>
      );
    }

    const chartFilteredRows = filter && filter.key === 'groupName'
      ? rows.filter(r => r.groupName === filter.value)
      : rows;

    const displayedRows = chartFilteredRows.filter(r =>
      matchesSearch(`${r.firstName} ${r.lastName}`, searchText) || matchesSearch(r.cardNumbers, searchText)
    );

    const searchFilterActive = !!searchText;

    let lastGroup = null;

    return (
      <div className="detail-table-wrap">
        {filter && filter.key === 'groupName' && (
          <div className="detail-filter-bar">
            <Filter size={14} />
            <span>Showing {chartFilteredRows.length} of {rows.length} cardholders</span>
            <span className="filter-value">Access level: {filter.value}</span>
            <button className="filter-clear-btn" onClick={onClearFilter}>
              <X size={12} /> Clear
            </button>
          </div>
        )}
        <div className="detail-search-bar">
          <input type="text" placeholder="Search cardholders..." value={searchText}
            onChange={e => setSearchText(e.target.value)} className="detail-search-input" />
        </div>
        <table className="detail-table">
          <thead>
            <tr>
              <th>Last name</th>
              <th>First name</th>
              <th>Card #(s)</th>
            </tr>
          </thead>
          <tbody>
            {searchFilterActive && displayedRows.length === 0 ? (
              <tr className="detail-no-results"><td colSpan={3}>No results match your filters.</td></tr>
            ) : displayedRows.map((row, i) => {
              const showGroupHeader = row.groupName !== lastGroup;
              lastGroup = row.groupName;
              return (
                <React.Fragment key={i}>
                  {showGroupHeader && (
                    <tr>
                      <th colSpan={3} className="td-name">{row.groupName}</th>
                    </tr>
                  )}
                  <tr>
                    <td className="td-name">{row.lastName}</td>
                    <td>{row.firstName}</td>
                    <td className="td-mono">{row.cardNumbers || '--'}</td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === 'EXPIRING_CARDS') {
    const rows = details?.expiringCardsTable || [];

    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    useEffect(() => {
      setSearchText('');
      setStatusFilter('');
    }, [rows]);

    if (!rows.length) {
      return (
        <div className="reports-empty-state">
          <Calendar size={48} />
          <h3>No cards expiring soon</h3>
          <p>No cardholder cards fall within the configured expiration window. Widen the threshold in report parameters to see more.</p>
        </div>
      );
    }

    const displayedRows = rows
      .filter(r => !statusFilter || (statusFilter === 'expired' ? r.expired === true : r.expired === false))
      .filter(r => matchesSearch(`${r.firstName} ${r.lastName}`, searchText) || matchesSearch(r.cardNumber, searchText));

    const searchFilterActive = !!(searchText || statusFilter);

    return (
      <div className="detail-table-wrap">
        <div className="detail-search-bar">
          <input type="text" placeholder="Search cardholders..." value={searchText}
            onChange={e => setSearchText(e.target.value)} className="detail-search-input" />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="expired">Expired</option>
            <option value="expiring">Expiring</option>
          </select>
        </div>
        <table className="detail-table">
          <thead>
            <tr>
              <th>Last name</th>
              <th>First name</th>
              <th>Card #</th>
              <th>Expires on</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {searchFilterActive && displayedRows.length === 0 ? (
              <tr className="detail-no-results"><td colSpan={5}>No results match your filters.</td></tr>
            ) : displayedRows.map((row, i) => (
              <tr key={i}>
                <td className="td-name">{row.lastName}</td>
                <td>{row.firstName}</td>
                <td className="td-mono">{row.cardNumber || '--'}</td>
                <td className="td-date">{row.expiresOn ? new Date(row.expiresOn).toLocaleDateString() : '--'}</td>
                <td className={row.expired ? 'td-warn' : ''}>{row.expired ? 'Expired' : 'Expiring'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === 'REGISTRATIONS_BY_DATE') {
    const rows = details?.registrationsTable || [];

    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    useEffect(() => {
      setSearchText('');
      setStatusFilter('');
    }, [rows]);

    if (!rows.length) {
      return (
        <div className="reports-empty-state">
          <Users size={48} />
          <h3>No registrations found</h3>
          <p>No registrations found in the selected date range. Try widening the date range in report parameters.</p>
        </div>
      );
    }

    const chartFilteredRows = filter && filter.key === 'day'
      ? rows.filter(r => r.dayKey === filter.value)
      : rows;

    const displayedRows = chartFilteredRows
      .filter(r => !statusFilter || (statusFilter === 'migrated' ? r.migrated === true : r.migrated === false))
      .filter(r => matchesSearch(`${r.firstName} ${r.lastName}`, searchText));

    const searchFilterActive = !!(searchText || statusFilter);

    return (
      <div className="detail-table-wrap">
        {filter && filter.key === 'day' && (
          <div className="detail-filter-bar">
            <Filter size={14} />
            <span>Showing {chartFilteredRows.length} of {rows.length} registrations</span>
            <span className="filter-value">Date: {new Date(filter.value).toLocaleDateString()}</span>
            <button className="filter-clear-btn" onClick={onClearFilter}>
              <X size={12} /> Clear
            </button>
          </div>
        )}
        <div className="detail-search-bar">
          <input type="text" placeholder="Search registrations..." value={searchText}
            onChange={e => setSearchText(e.target.value)} className="detail-search-input" />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="registered">Registered</option>
            <option value="migrated">Migrated</option>
          </select>
        </div>
        <table className="detail-table">
          <thead>
            <tr>
              <th>Last name</th>
              <th>First name</th>
              <th>Registered on</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {searchFilterActive && displayedRows.length === 0 ? (
              <tr className="detail-no-results"><td colSpan={4}>No results match your filters.</td></tr>
            ) : displayedRows.map((row, i) => (
              <tr key={i}>
                <td className="td-name">{row.lastName}</td>
                <td>{row.firstName}</td>
                <td className="td-date">{row.registeredOn ? new Date(row.registeredOn).toLocaleDateString() : '--'}</td>
                <td className={row.migrated ? 'td-warn' : ''}>{row.migrated ? 'Migrated' : 'Registered'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === 'DEVICE_MONITOR_EVENTS') {
    const rows = details?.eventsTable || [];

    const [searchText, setSearchText] = useState('');
    const [severityFilter, setSeverityFilter] = useState('');

    useEffect(() => {
      setSearchText('');
      setSeverityFilter('');
    }, [rows]);

    if (!rows.length) {
      return (
        <div className="reports-empty-state">
          <Monitor size={48} />
          <h3>No monitor events found</h3>
          <p>No monitor events found in the selected date range and filters.</p>
        </div>
      );
    }

    const severities = [...new Set(rows.map(r => r.severity).filter(Boolean))].sort();

    const chartFilteredRows = filter && filter.key === 'date'
      ? rows.filter(r => r.timestamp && new Date(r.timestamp).toISOString().split('T')[0] === filter.value)
      : filter && filter.key === 'conditionType'
      ? rows.filter(r => r.conditionType === filter.value)
      : rows;

    const displayedRows = chartFilteredRows
      .filter(r => !severityFilter || r.severity === severityFilter)
      .filter(r => matchesSearch(r.deviceName, searchText) || matchesSearch(r.message, searchText));

    const searchFilterActive = !!(searchText || severityFilter);

    return (
      <div className="detail-table-wrap">
        {filter && (filter.key === 'date' || filter.key === 'conditionType') && (
          <div className="detail-filter-bar">
            <Filter size={14} />
            <span>Showing {chartFilteredRows.length} of {rows.length} events</span>
            <span className="filter-value">
              {filter.key === 'date' ? `Date: ${new Date(filter.value).toLocaleDateString()}` : `Condition: ${filter.value}`}
            </span>
            <button className="filter-clear-btn" onClick={onClearFilter}>
              <X size={12} /> Clear
            </button>
          </div>
        )}
        <div className="detail-search-bar">
          <input type="text" placeholder="Search device or message..." value={searchText}
            onChange={e => setSearchText(e.target.value)} className="detail-search-input" />
          <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}>
            <option value="">All severities</option>
            {severities.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <table className="detail-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Severity</th>
              <th>Condition</th>
              <th>Device</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {searchFilterActive && displayedRows.length === 0 ? (
              <tr className="detail-no-results"><td colSpan={5}>No results match your filters.</td></tr>
            ) : displayedRows.map((row, i) => (
              <tr key={i}>
                <td className="td-date">{row.timestamp ? new Date(row.timestamp).toLocaleString() : '--'}</td>
                <td className={row.severity === 'CRITICAL' ? 'td-warn' : ''}>{row.severity}</td>
                <td>{row.conditionType}</td>
                <td className="td-name">{row.deviceName}</td>
                <td>{row.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="reports-empty-state">
      <FileText size={48} />
      <h3>Detailed view not available</h3>
      <p>Detailed breakdown is not yet available for this report type. Check back after the next update.</p>
    </div>
  );
}

// ============================================
// Raw Data View
// ============================================
function RawView({ data }) {
  return (
    <pre className="raw-data-view">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

// ============================================
// Helpers
// ============================================
function getTypeColor(type) {
  const colors = {
    SYSTEM_HEALTH: '#00d4aa',
    RECORDING_ASSURANCE: '#3b82f6',
    ACCESS_CONTROL: '#a78bfa',
    FACIAL_RECOGNITION: '#ec4899',
    USER_ACTIVITY: '#f59e0b',
    SITE_SUMMARY: '#ef4444',
    ACCESS_CONTROL_USERS: '#a78bfa',
    EXPIRING_CARDS: '#f59e0b',
    REGISTRATIONS_BY_DATE: '#22c55e',
    DEVICE_MONITOR_EVENTS: '#00d4aa',
  };
  return colors[type] || '#64748b';
}

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}
